import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification, deleteUser, getAdditionalUserInfo, signOut } from 'firebase/auth';
import { auth, signInWithGooglePopup, isFirebaseConfigured, firebaseConfigError } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import getFirebaseErrorMessage from '../utils/firebaseErrors';
import axios from 'axios';
import CustomSelect from './ui/CustomSelect';
import useBackgroundImagePreload from '../hooks/useBackgroundImagePreload';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

const SignUp = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    phone: '',
    city: '',
    gender: '',
    qualification: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const backgroundImageSrc = '/registraion-bg.png';
  const isBackgroundLoaded = useBackgroundImagePreload(backgroundImageSrc);
  const redirectTo = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const saveUserProfile = async (uid, userData) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      await axios.post(`${API_BASE_URL}/api/save_user_profile`, {
        uid,
        ...userData
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Failed to save user profile:', error);
      throw new Error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        'We could not complete your signup because your profile could not be saved. Please try again.'
      );
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();

    if (!isFirebaseConfigured || !auth) {
      setError(firebaseConfigError || 'Firebase is not configured.');
      return;
    }

    setLoading(true);
    setError('');

    if (!formData.gender || !formData.qualification) {
      setError('Please fill all required fields.');
      setLoading(false);
      return;
    }
    
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;
      
      try {
        // Save additional user details to database before treating signup as complete.
        await saveUserProfile(user.uid, {
          fullName: formData.fullName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          city: formData.city.trim(),
          gender: formData.gender.trim(),
          qualification: formData.qualification.trim(),
          provider: 'email'
        });
      } catch (profileError) {
        try {
          await deleteUser(user);
        } catch (rollbackError) {
          console.error('Failed to rollback Firebase user after profile save error:', rollbackError);
        }

        throw profileError;
      }
      
      await sendEmailVerification(user);
      await signOut(auth);
      
      setVerificationSent(true);
      navigate('/verify-email');
    } catch (error) {
      setError(error.message || getFirebaseErrorMessage(error));
    }
    setLoading(false);
  };

  const handleGoogleSignUp = async () => {
    if (!isFirebaseConfigured || !auth) {
      setError(firebaseConfigError || 'Firebase is not configured.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const result = await signInWithGooglePopup();
      const user = result.user;
      const additionalUserInfo = getAdditionalUserInfo(result);
      const isNewGoogleUser = additionalUserInfo?.isNewUser;
      
      try {
        // Save Google user profile before allowing the session to continue.
        await saveUserProfile(user.uid, {
          fullName: user.displayName || '',
          email: user.email,
          phone: '',
          city: '',
          gender: '',
          qualification: '',
          provider: 'google'
        });
      } catch (profileError) {
        if (isNewGoogleUser) {
          try {
            await deleteUser(user);
          } catch (rollbackError) {
            console.error('Failed to rollback Google user after profile save error:', rollbackError);
          }
        }

        await signOut(auth);
        throw profileError;
      }
    } catch (error) {
      console.error('Google sign up error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-up cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup blocked. Please allow popups and try again.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        setError('Another sign-up popup was already in progress. Please try again.');
      } else {
        setError(error.message || getFirebaseErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[linear-gradient(135deg,#f8f4fb,#fff8f3)]">
      <div
        className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-300 ease-out ${
          isBackgroundLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ backgroundImage: `url('${backgroundImageSrc}')` }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(248,244,251,0.62),rgba(255,248,243,0.52))]" />
      <div className="relative flex min-h-screen items-center justify-center px-5 py-6 sm:px-6 sm:py-8">
      <div className="max-w-md w-full space-y-5 p-6 bg-white/95 rounded-lg shadow-md backdrop-blur-sm">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">Create Account</h2>
          <p className="mt-1 text-gray-600">Start your college journey today</p>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        {verificationSent ? (
          <div className="text-center space-y-3">
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md text-sm">
              Redirecting to verification page...
            </div>
          </div>
        ) : (
          <>
            <form onSubmit={handleEmailSignUp} className="space-y-3">
              <div>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Full Name"
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email address"
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Password (min 6 characters)"
                  required
                  minLength="6"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <CustomSelect
                  id="signup-gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  placeholder="Select Gender"
                  options={[
                    { value: 'Male', label: 'Male' },
                    { value: 'Female', label: 'Female' },
                    { value: 'Other', label: 'Other' }
                  ]}
                  buttonClassName="px-3 py-2 rounded-md border-gray-300"
                />
              </div>
              <div>
                <CustomSelect
                  id="signup-qualification"
                  name="qualification"
                  value={formData.qualification}
                  onChange={handleChange}
                  placeholder="Select Qualification"
                  options={[
                    { value: '12th', label: '12th Grade' },
                    { value: 'Diploma', label: 'Diploma' },
                    { value: 'Bachelor', label: "Bachelor's Degree" },
                    { value: 'Master', label: "Master's Degree" },
                    { value: 'PhD', label: 'PhD' }
                  ]}
                  buttonClassName="px-3 py-2 rounded-md border-gray-300"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-gradient-to-r from-[#9a51a0] to-[#f36f21] text-white rounded-md font-medium hover:opacity-90 disabled:opacity-50"
              >
                {loading ? 'Creating Account...' : 'Sign Up'}
              </button>
            </form>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or</span>
              </div>
            </div>
            
            <button
              onClick={handleGoogleSignUp}
              disabled={loading}
              className="w-full flex justify-center items-center px-4 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <button
                  onClick={() => navigate('/signin', { state: { from: { pathname: redirectTo } } })}
                  className="font-medium text-[#9a51a0] hover:text-[#f36f21] transition-colors"
                >
                  Sign in here
                </button>
              </p>
            </div>
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default SignUp;
